// @flow
/* eslint-disable ta-code-conventions/has-matching-test */
import React, { memo, useCallback, useContext, useLayoutEffect, useRef, type Node } from 'react';
import classNames from '@ta/common.classnames';
import { useVisibleEffect } from '@ta/common.is-visible';
import Icon from '@ta/core-ui.icon';
import { Device } from '@ta/lithium.context';
import { useLocalize } from '@ta/platform.i18n';
import { useBlockScroll } from '@ta/modal.core';
import SearchResult from './SearchResult';
import { type TypeaheadItem } from './types';
import styles from './TypeaheadUI.css';

/**
 * The background color to use. Defaults to white but can be gray to have some contrast against
 * white backgrounds.
 */
export type TypeaheadColor = 'white' | 'gray';

/**
 * The size and style to use.
 */
export type TypeaheadStyle =
  // A large box, used when the typeahead is in the hero
  | 'box'
  // An underlined text input, used when the typeahead is in the hero
  | 'underlined'
  // A small box or icon that expands when focused, used when the typeahead is in the global nav
  | 'expanding';

type Props = {|
  /**
   * What color scheme to use
   */
  color?: TypeaheadColor,

  /**
   * The size and style to use.
   */
  styleVariant?: TypeaheadStyle,

  /**
   * Ghost text to show while nothing is entered in the search box
   */
  placeholderText?: string,

  /**
   * A component displayed while the typeahead overlay instead of the passed in results and rescue
   * query, or null to show the results
   */
  resultsPlaceholder?: Node,

  /** The current query entered in the search input */
  query?: string,

  /** The current geo scope id */
  geoId?: ?number,

  /** Callback for when the search input changes */
  onChange?: string => mixed,

  /** Callback for when the typeahead overlay is manually focused */
  onOpen?: () => mixed,

  /** Callback for when the typeahead overlay is manually dismissed */
  onClose?: () => mixed,

  /** Callback for when the typeahead gains keyboard focus */
  onFocus?: (SyntheticFocusEvent<HTMLElement>) => mixed,

  /** Callback for when the typeahead loses keyboard focus */
  onBlur?: (SyntheticFocusEvent<HTMLElement>) => mixed,

  /** Callback for when a key is pressed */
  onKeyDown?: (SyntheticKeyboardEvent<HTMLElement>) => mixed,

  /** Callback for when the search query is submitted */
  onSubmit?: (SyntheticEvent<HTMLFormElement>) => mixed,

  /** Callback for when a result is hovered over */
  onResultHovered?: (SyntheticMouseEvent<HTMLAnchorElement>) => mixed,

  /** Callback for when a result is clicked */
  onResultClicked?: TypeaheadItem => mixed,

  /**
   * True to show the overlay containing the results, or the resultsPlaceholder if it's passed in
   */
  isOpen?: boolean,

  /** True to show a loading spinner */
  isLoading?: boolean,

  /** The result selected with keyboard navigation, or null if no result has been selected */
  selectedIndex?: ?number,

  /** The results from the typeahead API */
  results?: TypeaheadItem[],

  /** The opaque ID string for this search session, for tracking */
  searchSessionId?: string,
|};

/**
 * The UI for the typeahead input
 *
 * The API interactions are in {@link ./useSearchResults}, and the callbacks and keyboard navigation
 * logic is defined in {@link ./TypeaheadUI}.
 */
const TypeaheadUI = ({
  color = 'white',
  styleVariant = 'box',
  placeholderText,
  resultsPlaceholder,
  query = '',
  geoId = 0,
  onChange = () => {},
  onOpen,
  onClose,
  onFocus,
  onBlur,
  onKeyDown,
  onSubmit,
  onResultHovered,
  onResultClicked,
  isOpen,
  isLoading,
  selectedIndex,
  results = [],
  searchSessionId = '',
}: Props) => {
  const { localize } = useLocalize();
  const { viewportCategory } = useContext(Device);

  const typeaheadRef = useRef<?HTMLElement>();
  const inputRef = useRef<?HTMLInputElement>();

  const isGeoscoped = typeof geoId === 'number' && geoId > 1;

  const displayedQuery = (selectedIndex != null && results[selectedIndex]?.title) || query;

  // Close the typeahead when it's scrolled out of the viewport. useVisibleEffect() does not
  // allow us to update the callback after mount, so store onClose in a ref.
  const closeCallback = useRef(onClose);
  closeCallback.current = onClose;
  const overlayRef = useVisibleEffect(isVisible => {
    if (!isVisible && closeCallback.current != null) {
      if (inputRef.current) {
        inputRef.current.blur();
      }
      closeCallback.current();
    }
  });

  const change = useCallback(
    (evt: SyntheticInputEvent<HTMLInputElement>) => onChange(evt.currentTarget.value),
    [onChange],
  );

  const clear = useCallback(() => {
    onChange('');

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onChange]);

  const isFullscreenOverlayOpen = viewportCategory === 'MOBILE' && isOpen;

  // On mobile, close the on-screen keyboard when anything else is touched. The keyboard takes up
  // a lot of screen space that can get in the way of search results.
  const onTouchMove = useCallback(() => {
    if (isFullscreenOverlayOpen && inputRef.current) {
      inputRef.current.blur();
    }
  }, [isFullscreenOverlayOpen]);

  // Block page scroll while the typeahead is open on mobile, since in that case it appears as a
  // fullscreen overflay.
  useBlockScroll(isFullscreenOverlayOpen, false);
  useLayoutEffect(() => {
    const input = inputRef.current;
    const typeahead = typeaheadRef.current;

    // When the fullscreen mobile overlay opens, make sure the search input is focused
    if (isFullscreenOverlayOpen && input != null && typeahead != null) {
      input.focus();
    }
  }, [isFullscreenOverlayOpen]);

  // As a special case, the expanding style on mobile collapses into an icon instead of an inline
  // text input. This allows it to fit in a space-constrained area (the global nav)
  if (styleVariant === 'expanding' && viewportCategory === 'MOBILE' && !isOpen) {
    return (
      <button
        className={`${styles.button} ${styles.collapsedIcon}`}
        title={localize.string('common_Search') /* Search */}
        aria-label={localize.string('common_Search') /* Search */}
        onClick={() => {
          // Explicitly resetting the scroll position prevents buggy browser behavior that
          // autoscrolls the typeahead out of view. Since this is usually only visible near the top
          // of the page and is used to navigate away, preserving the scroll position of the page
          // isn't that important.
          // eslint-disable-next-line no-restricted-globals
          window.scrollTo({ top: 0, behavior: 'instant' });
          if (onOpen != null) {
            onOpen();
          }
        }}
      >
        <Icon name="search" />
      </button>
    );
  }

  return (
    <div
      ref={typeaheadRef}
      className={classNames(styles.typeahead, {
        [styles.open]: isOpen,
        [styles.gray]: color === 'gray',
        [styles.box]: styleVariant === 'box',
        [styles.underlined]: styleVariant === 'underlined',
        [styles.expanding]: styleVariant === 'expanding',
      })}
      onTouchMove={onTouchMove}
    >
      <div className={styles.backdrop} onClick={onClose} />

      <form
        className={styles.overlay}
        action="/Search"
        onFocus={onFocus}
        onBlur={onBlur}
        onInvalid={evt => evt.preventDefault()}
        onSubmit={onSubmit}
        ref={overlayRef}
      >
        <input
          ref={inputRef}
          type="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          required
          name="q"
          className={classNames(styles.textInput, {
            /* eslint-disable-next-line css-modules/no-undef-class */
            [styles.hasClearButton]: query.length !== 0,
          })}
          placeholder={placeholderText}
          title={localize.string('common_Search') /* Search */}
          aria-label={localize.string('common_Search') /* Search */}
          value={displayedQuery}
          onChange={change}
          onKeyDown={onKeyDown}
        />

        <input type="hidden" name="searchSessionId" value={searchSessionId} />

        {isGeoscoped && <input type="hidden" name="geo" value={geoId} />}

        <button
          type="button"
          className={`${styles.button} ${styles.back}`}
          title={localize.string('mobile_back_8e0') /* Back */}
          aria-label={localize.string('mobile_back_8e0') /* Back */}
          onClick={onClose}
        >
          <Icon name="arrow-left" />
        </button>

        {query.length !== 0 && (
          <button
            type="button"
            className={`${styles.button} ${styles.clear}`}
            title={localize.string('mobile_search_filter_clear_26e8') /* Clear */}
            aria-label={localize.string('mobile_search_filter_clear_26e8') /* Clear */}
            onClick={clear}
          >
            <Icon name="times" />
          </button>
        )}

        <button
          type="submit"
          formAction="/Search"
          className={`${styles.button} ${styles.search}`}
          title={localize.string('common_Search') /* Search */}
          aria-label={localize.string('common_Search') /* Search */}
        >
          {isLoading ? (
            <svg className={styles.loadingSpinner} viewBox="0 0 20 20">
              <path className={styles.circle} d="M 10 2 A 8 8 0 1 0 18 10" />
            </svg>
          ) : (
            <Icon name="search" />
          )}
        </button>

        {isOpen && (
          <div className={styles.results}>
            {resultsPlaceholder != null ? (
              resultsPlaceholder
            ) : (
              <>
                {results.map((result, index) => (
                  <SearchResult
                    key={result.key}
                    result={result}
                    selected={index === selectedIndex}
                    onHover={onResultHovered}
                    onClick={onResultClicked}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

// eslint-disable-next-line ta-code-conventions/require-jsdoc
export default memo<Props, *>(TypeaheadUI);
